import Img from "./Image";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components/macro";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Alert from "@material-ui/lab/Alert";

import FileUpload from "./FileUpload";
import ImageIcon from "@material-ui/icons/Image";
import { useDropzone } from "react-dropzone";
import { MAX_IMAGE_UPLOAD_SIZE_BYTES } from "./constConfig/config";
import ImageEditor from "./ImageEditor";

const defaultStyles = {
  borderRadius: "4px",
  height: "unset",
  maxHeight: "unset",
  maxWidth: "100%",
  width: 200,
};

const ImageContainer = styled.div`
  display: flex;
  justify-content: ${({ center }) => (center ? "center" : "flex-start")};
  align-items: center;
  width: 100%;
  > img {
    width: ${({ styles }) =>
      Number.isFinite(styles.width) ? `${styles.width}px` : styles.width};
    height: ${({ styles }) =>
      Number.isFinite(styles.height) ? `${styles.height}px` : styles.height};
    max-width: ${({ styles }) =>
      Number.isFinite(styles.maxWidth)
        ? `${styles.maxWidth}px`
        : styles.maxWidth};
    max-height: ${({ styles }) =>
      Number.isFinite(styles.maxHeight)
        ? `${styles.maxHeight}px`
        : styles.maxHeight};
    border-radius: ${({ styles }) =>
      Number.isFinite(styles.borderRadius)
        ? `${styles.borderRadius}px`
        : styles.borderRadius};
  }
`;

const Placeholder = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-height: 150px;
  height: 150px;
  border: ${({ isDragActive }) => (isDragActive ? 3 : 2)}px #e0e0e0 dashed;

  font-weight: ${({ isDragActive }) => (isDragActive ? "bold" : "normal")};
  p,
  span {
    font-weight: ${({ isDragActive }) => (isDragActive ? "bold" : "normal")};
  }
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  color: #bfbfbf;
  padding: 35px 73px;
`;

const ImagePlaceholder = styled.div`
  box-sizing: border-box;
  width: 100%;
  border: ${({ isDragActive }) => (isDragActive ? 3 : 2)}px #e0e0e0 dashed;
  font-weight: ${({ isDragActive }) => (isDragActive ? "bold" : "normal")};
  p,
  span {
    font-weight: ${({ isDragActive }) => (isDragActive ? "bold" : "normal")};
  }
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  color: #bfbfbf;
  padding: 15px 25px;
`;

const Container = ({
  cropShape = "rect",
  isLoading,
  image,
  containerStyles,
  styles,
  caption,
  buttonProps,
  onStartUpload,
  disabled,
  isUploading,
  handleRemove,
  editButtonProps,
  onUpload,
  error,
  center,
  showRemoveButton = true,
  showEditButton = false,
  initialCropHeight,
  initialinitialCropWidth,
  showUploadArea = true,
  showGalleryButton = false,
  onOpenGallery,
  onImageRetryStateChange,
  galleryButtonProps = { children: "Gallery" },
  isPreviewRetrying = false,
}) => {
  const { getRootProps, isDragActive } = useDropzone({
    onDrop: onUpload,
    disabled,
  });

  if (!showUploadArea) {
    return (
      <Grid container item spacing={2} xs={12}>
        <Grid item xs={12}>
          <Grid container justifyContent="center" spacing={2}>
            <Grid item>
              <Button
                color="primary"
                disabled={disabled || isUploading}
                endIcon={isUploading && <CircularProgress size={25} />}
                variant="contained"
                onClick={onStartUpload}
                {...buttonProps}
              />
              {error && (
                <div>
                  <Typography color="error" variant="caption">
                    {error}
                  </Typography>
                </div>
              )}
            </Grid>
            {isUploading && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Upload in progress. The file is being scanned for security and
                  may take a few minutes to become available.
                </Alert>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container item spacing={2} xs={12}>
      <Grid item xs={12}>
        {isLoading ? (
          <Placeholder>
            <CircularProgress size={30} />
          </Placeholder>
        ) : image ? (
          <Img
            retryOnError
            container={(children) => (
              <div {...getRootProps()}>
                {isDragActive ? (
                  <Placeholder isDragActive={isDragActive}>
                    <Typography
                      color="textSecondary"
                      style={{ color: "#BFBFBF" }}
                      variant="body2"
                    >
                      Drop to upload
                    </Typography>
                  </Placeholder>
                ) : (
                  <ImageContainer
                    center={center}
                    style={containerStyles || {}}
                    styles={styles}
                  >
                    {children}
                  </ImageContainer>
                )}
              </div>
            )}
            loader={
              <Placeholder>
                <CircularProgress size={30} />
                {isPreviewRetrying && (
                  <Typography color="textSecondary" variant="caption">
                    File is being scanned. Retrying shortly...
                  </Typography>
                )}
              </Placeholder>
            }
            retryDelayMs={10000}
            retryMaxAttempts={3}
            src={[image]}
            unloader={
              <Placeholder {...getRootProps()} isDragActive={isDragActive}>
                <Typography variant="caption">
                  Image is broken or missed
                </Typography>
                <Typography color="textSecondary" variant="caption">
                  Try reloading the page and upload again if needed.
                </Typography>
              </Placeholder>
            }
            onRetryStateChange={onImageRetryStateChange}
          />
        ) : (
          <ImagePlaceholder {...getRootProps()} isDragActive={isDragActive}>
            {caption}
          </ImagePlaceholder>
        )}
      </Grid>
      <Grid item xs={12}>
        <Grid
          container
          justifyContent={!showRemoveButton ? "center" : "flex-start"}
          spacing={2}
        >
          <Grid
            item
            xs={
              buttonProps.fullWidth
                ? showRemoveButton && showGalleryButton
                  ? 4
                  : showRemoveButton || showGalleryButton
                  ? 6
                  : 12
                : undefined
            }
          >
            <Button
              color="primary"
              disabled={disabled || isUploading}
              endIcon={isUploading && <CircularProgress size={25} />}
              variant="contained"
              onClick={onStartUpload}
              {...buttonProps}
            />
            {error && (
              <div>
                <Typography color="error" variant="caption">
                  {error}
                </Typography>
              </div>
            )}
          </Grid>
          {showGalleryButton && (
            <Grid item xs={buttonProps.fullWidth ? 4 : undefined}>
              <Button
                color="default"
                disabled={disabled || isUploading}
                variant="contained"
                onClick={onOpenGallery}
                {...galleryButtonProps}
              >
                {galleryButtonProps?.children || "Gallery"}
              </Button>
            </Grid>
          )}
          {showRemoveButton && (
            <Grid item xs={buttonProps.fullWidth ? 4 : undefined}>
              <Button
                disabled={disabled || !image}
                variant="contained"
                onClick={handleRemove}
                {...buttonProps}
              >
                Remove
              </Button>
            </Grid>
          )}

          {showEditButton && (
            <Grid item xs={buttonProps.fullWidth ? 6 : undefined}>
              <Button
                disabled={disabled || !image}
                variant="contained"
                onClick={editButtonProps?.onClick}
                {...buttonProps}
              >
                Edit
              </Button>
            </Grid>
          )}
          {isUploading && (
            <Grid item xs={12}>
              <Alert severity="info">
                Upload in progress. The file is being scanned for security and
                may take a few minutes to become available.
              </Alert>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

const ImageUpload = ({
  cropShape = "rect",
  onChange,
  value,
  originalImage,
  disabled,
  errorMessage,
  isLoading,
  maxSize = MAX_IMAGE_UPLOAD_SIZE_BYTES,
  caption = <ImageIcon />,
  buttonProps = { children: "Upload", fullWidth: true },
  onReset,
  containerStyles,
  center = false,
  showRemoveButton = true,
  showEditButton = false, // default do not show edit button
  initialCropHeight,
  initialCropWidth,
  showUploadArea = true,
  multiple = false,
  showGalleryButton = false,
  galleryButtonProps,
  renderGallery, // ({ open, onClose, onSelect }) => ReactNode
  ...props
}) => {
  const [error, setError] = useState(errorMessage);
  const [image, setImage] = useState(value);
  const [isUploading, setIsUploading] = useState(false);
  const [isPreviewRetrying, setIsPreviewRetrying] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editKey, setEditKey] = useState(0); // force remount of editor when restoring

  const styles = useMemo(
    () => ({
      ...defaultStyles,
      ...props.styles,
    }),
    [props.styles]
  );

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const handleOpenGallery = useCallback(() => setIsGalleryOpen(true), []);
  const handleCloseGallery = useCallback(() => setIsGalleryOpen(false), []);

  const handleSelectFromGallery = useCallback(
    (selection) => {
      // selection can be an image object, an array of images, or a simple {url, name}
      if (!onChange) return;
      if (Array.isArray(selection)) {
        const mapped = selection
          .map((it) => {
            if (!it) return null;
            if (typeof it === "string") return { url: it };
            const url = it.url || it.imageUrl || it.image_url || it.src;
            const name = it.name || it.fileName || it.filename || undefined;
            return url ? { url, name } : null;
          })
          .filter(Boolean);
        onChange(mapped);
      } else {
        const it = selection || {};
        const url = it.url || it.imageUrl || it.image_url || it.src;
        const name = it.name || it.fileName || it.filename || undefined;
        onChange(url ? { url, name } : { url: "" });
      }
      setIsGalleryOpen(false);
    },
    [onChange]
  );

  useEffect(() => {
    setImage(value);
  }, [value]);

  useEffect(() => {
    setError(errorMessage);
  }, [errorMessage]);

  const handleImageUpload = useCallback(
    async (args) => {
      // args can be a single file object or an array when multiple is true
      if (Array.isArray(args)) {
        if (!onChange) return;
        const normalizedArgs = args.map((item) => ({
          ...item,
          source: item?.source || "upload",
        }));
        try {
          setIsUploading(true);
          setError(null);
          setIsPreviewRetrying(false);
          await onChange(normalizedArgs);
        } catch (err) {
          setError(err?.message || "Upload failed");
        } finally {
          setIsUploading(false);
        }
        return;
      }

      const { body, type, name, source } = args || {};
      if (!onChange) {
        setImage(body);
        return;
      }

      try {
        setIsUploading(true);
        setError(null);
        setIsPreviewRetrying(false);
        await onChange({ body, name, type, source: source || "upload" });
      } catch (err) {
        setImage(value);
        setIsPreviewRetrying(false);
        setError(err?.message || "Upload failed");
      } finally {
        setIsUploading(false);
      }
    },
    [onChange, value]
  );

  // Replace image with the new cropped one
  const handleCropSave = useCallback(
    ({ url, source }) => {
      if (!url) {
        setEditOpen(false);
        return;
      }

      // If restoring, do not re-upload; just set and propagate the existing URL.
      if (source === "restore") {
        setImage(url);
        if (onChange) {
          onChange({ url, source: "restore" });
        }
        // Remount the editor to reset crop state while keeping the modal open
        setEditKey((k) => k + 1);
        return;
      }

      handleImageUpload({
        body: url,
        name: `cropped-image-${Date.now()}.png`,
        source: "crop",
        type: "image/png",
      });

      setEditOpen(false);
    },
    [handleImageUpload, onChange]
  );

  const handleRemove = useCallback(() => {
    setImage(null);
    setIsPreviewRetrying(false);
    if (!onChange) {
      return;
    }
    onChange({ body: null });
  }, [onChange, setImage]);

  const handlePreviewRetryStateChange = useCallback(({ isRetrying }) => {
    setIsPreviewRetrying(Boolean(isRetrying));
  }, []);

  return (
    <>
      <FileUpload
        accept=".png, .jpg, .jpeg" // or image/png, image/jpeg, but later on I am checking based on extension
        cropShape={cropShape}
        initialCropHeight={initialCropHeight}
        initialCropWidth={initialCropWidth}
        maxSize={maxSize}
        multiple={multiple}
        readAs="DataURL"
        showEditButton={showEditButton}
        onChange={handleImageUpload}
        onError={setError}
      >
        {({ upload, onUpload }) => (
          <>
            <Container
              buttonProps={buttonProps}
              caption={caption}
              center={center}
              containerStyles={containerStyles}
              cropShape={cropShape}
              disabled={disabled}
              editButtonProps={{
                onClick: () => setEditOpen(true),
              }}
              error={error}
              galleryButtonProps={galleryButtonProps}
              handleRemove={handleRemove}
              image={image}
              isLoading={isLoading}
              isPreviewRetrying={isPreviewRetrying}
              isUploading={isUploading}
              showEditButton={showEditButton}
              showGalleryButton={showGalleryButton}
              showRemoveButton={showRemoveButton}
              showUploadArea={showUploadArea}
              styles={styles}
              onImageRetryStateChange={handlePreviewRetryStateChange}
              onOpenGallery={handleOpenGallery}
              onStartUpload={upload}
              onUpload={onUpload}
            />
            {typeof renderGallery === "function" &&
              renderGallery({
                open: isGalleryOpen,
                onClose: handleCloseGallery,
                onSelect: handleSelectFromGallery,
              })}
          </>
        )}
      </FileUpload>
      <ImageEditor
        key={editKey}
        cropShape={cropShape}
        image={image}
        initialCropHeight={initialCropHeight}
        initialCropWidth={initialCropWidth}
        open={editOpen}
        originalImage={originalImage}
        onClose={() => setEditOpen(false)}
        onCropSave={handleCropSave}
      />
    </>
  );
};

export default memo(ImageUpload);
